import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AdminHeader from "@/components/admin/AdminHeader";
import AddEliteUserDialog from "@/components/admin/AddEliteUserDialog";
import AdminUserTable from "@/components/admin/AdminUserTable";

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  tier: string;
  subscription_status: string;
}

export default function Admin() {
  const { user, session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Check admin role
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth?redirect=/admin");
      return;
    }
    if (user) {
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle()
        .then(({ data }) => {
          if (!data) {
            navigate("/");
            toast.error("Admin access required");
          } else {
            setIsAdmin(true);
          }
        });
    }
  }, [user, isLoading, navigate]);

  const fetchUsers = async () => {
    if (!session) return;
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-list-users", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      setUsers(data.users || []);
    } catch (e: any) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isAdmin && session) fetchUsers();
  }, [isAdmin, session]);

  if (isLoading || isAdmin === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <AdminHeader onAddEliteUser={() => setShowAddDialog(true)} />
        <AdminUserTable users={users} loading={loadingUsers} onRefresh={fetchUsers} />
        <AddEliteUserDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          onSuccess={fetchUsers}
        />
      </div>
    </div>
  );
}
